import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { spacing } from 'c1ui/theme/C1Spacing'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { useAlert } from 'hooks/useAlert'
import { createSurfaceFeedbackApi } from 'lib/actions/a2ui'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { A2UISurfaceFeedbackSentiment } from 'pbts/c1/api/a2ui/v1/a2ui'
import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type SurfaceFeedbackProps = {
    conversationId: string
    surfaceId: string
}

/**
 * SurfaceFeedback provides thumbs up/down feedback for A2UI surfaces.
 * Captures a snapshot of the surface state at feedback time.
 */
export const SurfaceFeedback: FC<SurfaceFeedbackProps> = ({ conversationId, surfaceId }) => {
    const intl = useIntl()
    const [sentiment, setSentiment] = useState<A2UISurfaceFeedbackSentiment | null>(null)
    const [feedbackText, setFeedbackText] = useState<string>('')
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
    const toast = useAlert()

    const {
        mutate: createFeedback,
        inProgress: isSubmitting,
    } = createSurfaceFeedbackApi()

    const handleThumbsClick = (type: A2UISurfaceFeedbackSentiment): void => {
        if (sentiment === type) {
            setSentiment(null)
        } else {
            setSentiment(type)
        }
    }

    const handleClose = (): void => {
        setFeedbackText('')
        setSentiment(null)
    }

    const handleSubmit = async (): Promise<void> => {
        if (sentiment === null) return

        try {
            await createFeedback({
                conversationId,
                surfaceId,
                sentiment,
                text: feedbackText,
            })
        } catch (err) {
            toast(
                intl.formatMessage(
                    {
                        id: 'a2ui.feedback.error',
                        defaultMessage: 'Failed to submit feedback. Please try again. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
            return
        }
        setIsSubmitted(true)
    }

    return <Box sx={{ mt: spacing.s }}>
        <Stack direction="row" spacing={1}>
            <Tooltip
                title={intl.formatMessage({
                    id: 'a2ui.feedback.thumbs_up',
                    defaultMessage: 'This was helpful',
                })}
                placement="top"
                disableHoverListener={isSubmitted}
            >
                <IconButton
                    size="small"
                    onClick={(): void => handleThumbsClick(A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_POSITIVE)}
                    aria-label={intl.formatMessage({
                        id: 'a2ui.feedback.thumbs_up',
                        defaultMessage: 'This was helpful',
                    })}
                    aria-pressed={sentiment === A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_POSITIVE}
                    disabled={isSubmitted}
                >
                    <C1Icon icon={ThumbsUp} size="sm" />
                </IconButton>
            </Tooltip>
            <Tooltip
                title={intl.formatMessage({
                    id: 'a2ui.feedback.thumbs_down',
                    defaultMessage: 'This was not helpful',
                })}
                placement="top"
                disableHoverListener={isSubmitted}
            >
                <IconButton
                    size="small"
                    onClick={(): void => handleThumbsClick(A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_NEGATIVE)}
                    aria-label={intl.formatMessage({
                        id: 'a2ui.feedback.thumbs_down',
                        defaultMessage: 'This was not helpful',
                    })}
                    aria-pressed={sentiment === A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_NEGATIVE}
                    disabled={isSubmitted}
                >
                    <C1Icon icon={ThumbsDown} size="sm" />
                </IconButton>
            </Tooltip>
        </Stack>

        {sentiment !== null &&
            <Box sx={{ mt: spacing.m }}>
                {isSubmitted
                    ? <Typography color="text.secondary" variant="unconfigured">
                        <FormattedMessage
                            id="a2ui.feedback.thanks"
                            defaultMessage="Thank you for your feedback."
                        />
                    </Typography>
                    : <Box sx={{ py: spacing.m, px: spacing.l, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <C1LabeledTextFieldWithPresets
                            name="feedback"
                            label={sentiment === A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_POSITIVE
                                ? intl.formatMessage({
                                    id: 'a2ui.feedback.label_positive',
                                    defaultMessage: 'What did you like? (optional)',
                                })
                                : intl.formatMessage({
                                    id: 'a2ui.feedback.label_negative',
                                    defaultMessage: 'What could be improved? (optional)',
                                })}
                            placeholder={intl.formatMessage({
                                id: 'a2ui.feedback.placeholder',
                                defaultMessage: 'Share your thoughts...',
                            })}
                            value={feedbackText}
                            onChange={(e): void => setFeedbackText(e.target.value)}
                            presets={sentiment === A2UISurfaceFeedbackSentiment.A2UI_SURFACE_FEEDBACK_SENTIMENT_POSITIVE
                                ? [
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.helpful', defaultMessage: 'Helpful and accurate' }),
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.clear', defaultMessage: 'Clear and easy to use' }),
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.solved', defaultMessage: 'Solved my problem' }),
                                ]
                                : [
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.inaccurate', defaultMessage: 'Inaccurate information' }),
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.confusing', defaultMessage: 'Confusing interface' }),
                                    intl.formatMessage({ id: 'a2ui.feedback.preset.missing', defaultMessage: 'Missing options' }),
                                ]}
                            setValue={setFeedbackText}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                        />

                        <Stack direction="row" spacing={1} sx={{ mt: spacing.m }} justifyContent="flex-end">
                            <C1Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                <FormattedMessage id="a2ui.feedback.cancel" defaultMessage="Dismiss" />
                            </C1Button>
                            <C1Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting || sentiment === null}
                            >
                                <FormattedMessage id="a2ui.feedback.submit" defaultMessage="Submit" />
                            </C1Button>
                        </Stack>
                    </Box>
                }
            </Box>
        }
    </Box>
}
